/**
 * Página de Información Organizativa - Portal de Transparencia
 * Estilo Awwwards Interior - Estructura organizativa de INTERCASA, S.A.
 */

import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Información Organizativa con estilo Awwwards
 * @returns {JSX.Element} Página de información organizativa
 */
const OrganizativeInfoPage = () => {
  const heroRef = useRef(null);
  const location = useLocation();

  /**
   * Efecto para hacer scroll al inicio de la página al cargar
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section - Estilo Portal Transparencia */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <Link 
              to="/transparencia"
              className="group inline-flex items-center text-white/60 hover:text-white mb-8 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium tracking-wide">Portal de Transparencia</span>
            </Link>
            
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] mb-6 drop-shadow-lg">
              Información en Materia<br />
              <span className="text-intercasa-yellow">Organizativa</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              Marco legal y normativa aplicable a INTERCASA, S.A.
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">

            {/* Normativa Aplicable */}
            <div id="normativa" className="scroll-mt-32">
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                  Marco Legal
                </span>
                <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                  Normativa aplicable a la entidad
                </h2>
            
              <div className="space-y-6">
                {/* Operaciones de la empresa */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                    En relación a las operaciones de la empresa
                  </h3>
                  <p className="text-intercasa-dark/70 leading-relaxed">
                    La legislación y normativa relevante sobre materia de industria y de actividad de fabricación, envasado y comercialización de productos alimenticios.
                  </p>
                </div>

                {/* Actividades subvencionadas */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                    En relación a las actividades subvencionadas
                  </h3>
                  <ul className="space-y-3 text-intercasa-dark/70">
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Decreto 36/2009, de 31 de marzo, por el que se establece el régimen general de subvenciones de la Comunidad Autónoma de Canarias (<a href="https://www.gobiernodecanarias.org/boc/2009/068/001.html" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Ley 11/2006, de 11 de diciembre, de la Hacienda Pública Canaria (<a href="https://www.boe.es/buscar/act.php?id=BOE-A-2007-3825" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Real Decreto 477/2021, de 29 de junio, por el que se aprueba la concesión directa a las comunidades autónomas y a las ciudades de Ceuta y Melilla de ayudas para la ejecución de diversos programas de incentivos ligados al autoconsumo y al almacenamiento, con fuentes de energía renovable, así como a la implantación de sistemas térmicos renovables en el sector residencial, en el marco del Plan de Recuperación, Transformación y Resiliencia (<a href="https://www.boe.es/diario_boe/txt.php?id=BOE-A-2021-10824" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>La Ley 4/2021, de 2 de agosto, para la agilización administrativa y la planificación, gestión y control de los fondos procedentes del instrumento europeo de recuperación denominado «Next Generation EU», en el ámbito de la Comunidad Autónoma de Canarias (<a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2021-14237" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Ley 50/1985, de 27 de diciembre, de incentivos regionales para la corrección de desequilibrios económicos interterritoriales (<a href="https://www.boe.es/buscar/doc.php?id=BOE-A-1986-85" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Real Decreto 303/2015, de 24 de abril, por el que se modifica el Reglamento de los incentivos regionales, de desarrollo de la Ley 50/1985, de 27 de diciembre, aprobado por el Real Decreto 899/2007, de 6 de julio (<a href="https://www.boe.es/diario_boe/txt.php?id=BOE-A-2015-4667" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Real Decreto 899/2007, de 6 de julio, por el que se aprueba el Reglamento de los incentivos regionales, de desarrollo de la Ley 50/1985, de 27 de diciembre (<a href="https://www.boe.es/buscar/act.php?id=BOE-A-2007-13908" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Ley 12/2014 de 26 de diciembre de Transparencia y Acceso a la Información Pública (<a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2015-1114" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Ley 19/2013 de 9 de octubre de Transparencia, acceso a la información pública y buen gobierno (<a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2013-12887" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Ley orgánica 3/2018 de 5 de diciembre de Protección de Datos Personales (<a href="https://www.boe.es/buscar/act.php?id=BOE-A-2018-16673" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                  </ul>
                </div>

                {/* Normativa mercantil/contable */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                    Con carácter general, la siguiente normativa mercantil/contable
                  </h3>
                  <ul className="space-y-3 text-intercasa-dark/70">
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Ley de Sociedades de Capital, cuyo Texto Refundido se aprobó por Real Decreto Legislativo 1/2010, de 2 de julio, el Código de Comercio y disposiciones complementarias y la restante legislación mercantil (<a href="https://www.boe.es/buscar/act.php?id=BOE-A-2010-10544" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Ley 22/2015, de 20 de julio, de Auditoría de Cuentas (<a href="https://www.boe.es/buscar/act.php?id=BOE-A-2015-8147" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>Real Decreto 1/2021, de 12 de enero, por el que se modifica el Plan General de Contabilidad aprobado por el Real decreto 1514/2007, de 16 de noviembre (<a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2021-1350" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a>).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>El resto de la normativa contable española que resulte de aplicación.</span>
                    </li>
                  </ul>
                </div>

                {/* Botones de descarga */}
                <div className="flex flex-wrap gap-4 justify-center mt-8">
                  <a 
                    href="/PortalTansparenciaIntercasa/NormativaIntercasa/NormativaAplicableIntercasa.pdf"
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white font-lato font-medium rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar PDF
                  </a>
                  <a 
                    href="/PortalTansparenciaIntercasa/NormativaIntercasa/NormativaAplicableIntercasa.odt"
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white font-lato font-medium rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar ODT
                  </a>
                </div>
              </div>
              </AnimatedSection>
            </div>

            {/* Estatutos Sociales */}
            <div id="estatutos" className="scroll-mt-32 mt-20">
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                  Documentación Corporativa
                </span>
                <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                  Estatutos Sociales
                </h2>

                <div className="space-y-6">
                  {/* Solicitud de estatutos */}
                  <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                      Estatutos por los que rige INTERCASA, S.A.
                    </h3>
                    <p className="text-intercasa-dark/70 leading-relaxed mb-4">
                      Se hace constar que se puede solicitar los estatutos sociales de INTERCASA, S.A., a través de la página oficial de Registradores de España, en el siguiente enlace:
                    </p>
                    <a 
                      href="https://sede.registradores.org/sede/sede-corpme-web/home" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-intercasa-red hover:underline font-medium mb-4"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      sede.registradores.org
                    </a>
                    <p className="text-intercasa-dark/70 leading-relaxed mb-4">
                      Dentro de la página, deberá solicitar certificación e incluir la denominación social de la sociedad (INTERCASA, S.A.) e incluir el Registro de Las Palmas de Gran Canaria y, una vez dentro, seleccionar la opción <strong>"Certificaciones – Certificación de estatutos sociales"</strong>.
                    </p>
                    <p className="text-intercasa-dark/70 leading-relaxed mb-6">
                      Asimismo, puede solicitar esta información enviando un correo electrónico a la siguiente dirección: <a href="mailto:notificaciones@grumasa.es" className="text-intercasa-red hover:underline font-medium">notificaciones@grumasa.es</a>
                    </p>
                  </div>
                </div>

                {/* Botones de descarga */}
                <div className="flex flex-wrap gap-4 justify-center mt-8">
                  <a 
                    href="/PortalTansparenciaIntercasa/EstatutosSociales/estatutos.pdf"
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white font-lato font-medium rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar PDF
                  </a>
                  <a 
                    href="/PortalTansparenciaIntercasa/EstatutosSociales/estatutos.odt"
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white font-lato font-medium rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar ODT
                  </a>
                </div>
              </AnimatedSection>
            </div>

            {/* Información General */}
            <div id="informacion-general" className="scroll-mt-32 mt-20">
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                  Nuestra Historia
                </span>
                <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                  Información General
                </h2>

                <div className="space-y-6">
                  {/* Historia */}
                  <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                    <div className="space-y-4 text-intercasa-dark/70 leading-relaxed">
                      <p>
                        INTERCASA, S.A. es una empresa industrial con sede en Gran Canaria, perteneciente al Grupo Martinón, cuya trayectoria se remonta a más de siete décadas de actividad en el ámbito de la transformación alimentaria y la producción industrial.
                      </p>
                      <p>
                        La compañía fue fundada en 1949 por los empresarios Vicente Calderón y Virgilio Suárez, iniciando su actividad en Las Palmas de Gran Canaria. Desde sus orígenes, INTERCASA, S.A. ha destacado por su capacidad de adaptación al cambio y su apuesta por la calidad y la innovación productiva.
                      </p>
                      <p>
                        En 1973, la empresa trasladó su fábrica a la zona de Juan Grande, en el sureste de Gran Canaria, con el objetivo de ampliar sus instalaciones y situarse estratégicamente cerca de las zonas de cultivo de tomate fresco, materia prima principal de su producción.
                      </p>
                      <p>
                        Durante la década de 1980, INTERCASA, S.A. experimentó un importante proceso de expansión, iniciando la exportación de sus productos tanto a la península como a mercados internacionales, entre ellos Estados Unidos y diversos países del continente africano.
                      </p>
                      <p>
                        En los últimos años, la compañía ha desarrollado un ambicioso plan de modernización y diversificación, incorporando nuevos procesos de producción, formatos de envasado y líneas de producto. Este proceso ha reforzado su posición como una empresa moderna, eficiente y comprometida con la sostenibilidad y la innovación.
                      </p>
                      <p>
                        Actualmente, INTERCASA, S.A. dispone de unas modernas instalaciones en una parcela de más de 20.000 m², con una fábrica de 7.000 m², dedicada a la transformación del tomate fresco y con una capacidad productiva superior a 7 millones de kilos anuales.
                      </p>
                      <p>
                        Consolidada como una de las principales industrias agroalimentarias del archipiélago, INTERCASA, S.A. combina la experiencia adquirida a lo largo de su historia con una visión orientada al futuro, basada en la eficiencia, la sostenibilidad y la excelencia en la gestión.
                      </p>
                    </div>
                  </div>

                  {/* Objeto Social */}
                  <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                      Objeto Social de INTERCASA, S.A.
                    </h3>
                    <ul className="space-y-3 text-intercasa-dark/70">
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>La fabricación, envasado y comercialización, tanto al por mayor como al por menor, de productos alimenticios.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>La explotación de patentes nacionales e internacionales de productos alimenticios.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>La explotación de fincas agrícolas.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>La construcción de edificaciones, reparación o restauración de fincas urbanas por cuenta propia o ajena ya para conservarlas, arrendarlas, venderlas en bloque o por plantas, pisos o locales.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>La realización y contratación de obras de saneamiento, riego, cultivos, plantaciones y la instalación de industrias básicas.</span>
                      </li>
                    </ul>
                  </div>

                  {/* Datos Registrales */}
                  <div className="bg-intercasa-dark rounded-2xl p-6 md:p-8 text-white">
                    <h3 className="font-baskerville font-bold text-intercasa-yellow text-xl mb-4">
                      Datos Registrales y de Contacto
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-white mb-3">Constitución</h4>
                        <p className="text-white/70 text-sm leading-relaxed">
                          Constituida en escritura pública número 2.545, el día 20 de agosto de 1949, ante el notario D. Cayetano Ochoa Marín, como sustituto de su compañero D. Ángel Sanz Fernández.
                        </p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-3">Registro Mercantil de Las Palmas</h4>
                        <ul className="text-white/70 text-sm space-y-1">
                          <li>Hoja GC-1.782</li>
                          <li>Tomo 649</li>
                          <li>Folio 189</li>
                          <li>C.I.F.: A-35.002.559</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-3">Domicilio Social</h4>
                        <p className="text-white/70 text-sm">
                          Carretera Gral. Del Sur, KM.42,5<br />
                          Barranco De Juncalillo, Juan Grande<br />
                          Las Palmas
                        </p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-3">Contacto</h4>
                        <ul className="text-white/70 text-sm space-y-1">
                          <li>Teléfono: <a href="tel:928732001" className="text-intercasa-yellow hover:underline">928 732 001</a></li>
                          <li>Fax: 928 732 126</li>
                          <li>Email: <a href="mailto:info@intercasacanarias.es" className="text-intercasa-yellow hover:underline">info@intercasacanarias.es</a></li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Botones de descarga Información General */}
                  <div className="flex flex-wrap gap-4 justify-center mt-8">
                    <a 
                      href="/PortalTansparenciaIntercasa/InformaciónGeneral/informacion.pdf"
                      download
                      className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white font-lato font-medium rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Descargar PDF
                    </a>
                    <a 
                      href="/PortalTansparenciaIntercasa/InformaciónGeneral/informacion.odt"
                      download
                      className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white font-lato font-medium rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Descargar ODT
                    </a>
                  </div>

                  {/* Organigrama */}
                  <div className="bg-intercasa-bone/30 rounded-2xl p-8 border border-intercasa-bone">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-6 text-center">
                      Organigrama
                    </h3>
                    <div className="text-center">
                      <img 
                        src="/images/organigrama/organigramaIntercasa.webp" 
                        alt="Organigrama INTERCASA, S.A."
                        className="max-w-full h-auto mx-auto rounded-xl shadow-lg"
                      />
                      <p className="text-sm text-intercasa-gray mt-6">
                        Estructura organizativa de INTERCASA, S.A. — Actualizado noviembre 2025
                      </p>
                    </div>
                    
                    {/* Botones de descarga */}
                    <div className="flex flex-wrap gap-4 justify-center mt-6">
                      <a 
                        href="/PortalTansparenciaIntercasa/organigramaIntercasa/organigrama.pdf" 
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Descargar PDF
                      </a>
                      <a 
                        href="/PortalTansparenciaIntercasa/organigramaIntercasa/organigrama.odt" 
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Descargar ODT
                      </a>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default OrganizativeInfoPage;
